import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/lang";
import { DELETE_UPDATED, deleteSections, deleteText } from "@/lib/deleteAccount";
import { scrollToTarget } from "@/lib/lenis";
import { EASE, Eyebrow, MaskLines, Reveal } from "@/components/motion";
import { DeletionForm } from "@/components/DeletionForm";

const Bullets = ({ items }) => (
  <ul className="mt-5 space-y-3">
    {items.map((li, j) => (
      <li key={j} className="flex gap-3 leading-relaxed text-white/70">
        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-[#22d3ee] to-[#5cf2a0]" />{li}
      </li>
    ))}
  </ul>
);

export default function DeleteAccount() {
  const { lang } = useLang();
  const d = deleteText[lang];
  const sections = deleteSections[lang];

  return (
    <div data-testid="delete-account-page" className="relative pb-24 pt-32 sm:pt-40">
      <div className="container-x">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
          <Link to="/" data-testid="delete-account-back-link" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-1" />ZenVio
          </Link>
          <Eyebrow className="mt-10">{d.eyebrow}</Eyebrow>
        </motion.div>
        <h1 key={lang} data-testid="delete-account-title" className="mt-6 font-display text-5xl font-extrabold leading-[0.9] tracking-[-0.045em] sm:text-7xl lg:text-8xl">
          <MaskLines lines={[d.title]} delay={0.1} />
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg">{d.sub}</p>
        <div className="mt-8 flex flex-wrap gap-x-10 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">
          <span>{d.updated}: {DELETE_UPDATED[lang]}</span>
          <span>ZenSeo · ZenSay · ZenSync · ZenGage</span>
        </div>

        <div className="mt-20 grid gap-16 lg:grid-cols-12">
          <aside className="self-start lg:sticky lg:top-32 lg:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{d.toc}</p>
            <ol className="mt-5 space-y-2.5 text-sm">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <button type="button" data-testid={`delete-toc-${s.id}`} onClick={() => scrollToTarget(`#${s.id}`)} className="group flex gap-3 text-left text-white/55 transition-colors hover:text-white">
                    <span className="font-mono text-xs text-white/30">{String(i + 1).padStart(2, "0")}</span>
                    <span className="transition-transform duration-500 group-hover:translate-x-1">{s.h}</span>
                  </button>
                </li>
              ))}
            </ol>
          </aside>
          <article className="space-y-14 lg:col-span-8 lg:col-start-5">
            {sections.map((s, i) => (
              <Reveal key={s.id + lang} y={24} id={s.id} data-testid={`delete-section-${s.id}`} className="scroll-mt-32 border-t border-white/10 pt-8">
                <h2 className="flex items-baseline gap-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  <span className="font-mono text-xs font-normal text-white/30">{String(i + 1).padStart(2, "0")}</span>{s.h}
                </h2>
                {s.p?.map((p, j) => <p key={j} className="mt-4 leading-relaxed text-white/65">{p}</p>)}
                {s.list && <Bullets items={s.list} />}
                {s.after?.map((p, j) => <p key={j} className="mt-5 leading-relaxed text-white/55">{p}</p>)}
                {s.form && <DeletionForm />}
              </Reveal>
            ))}
          </article>
        </div>
      </div>
    </div>
  );
}
